import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Table, Form } from "react-bootstrap";
import axios from "../../../interceptor/axios-config";
import Swal from "sweetalert2";
import config from "../../../config";
import ThermalReceipt from "../../../utils/ThermalReceipt";
import ReactToPrint from "react-to-print";
import { useSelector, useDispatch } from "react-redux";
import { fetchLogo } from "../../../store/actions/logoActions";

const StudentLedgerModal = ({ show, onClose, student }) => {

  // for logo 
  const dispatch = useDispatch();
  // const { logo } = useSelector(state => state.logo);
  const { logoUrl } = useSelector(state => state.logo);


const studentId = student?.student_id;
const studentName = student?.full_name || "-";
const studentClass = student?.class_admitted || "-";

const [ledger, setLedger] = useState([]);
const [loading, setLoading] = useState(false);

const [paying, setPaying] = useState({});
const [selected, setSelected] = useState({});
const [selectAll, setSelectAll] = useState(false);

// ✅ receipt state
const [receiptData, setReceiptData] = useState(null);
const [lastReceipt, setLastReceipt] = useState(null);
const canReprint = ledger.some(row => row.status === "paid");


const receiptRef = useRef();
const printBtnRef = useRef();

// load logo 
useEffect(() => {
    if (!logoUrl) {
      dispatch(fetchLogo());
    }
  }, [logoUrl, dispatch]);
console.log(logoUrl);
// -----------------------------
// LOAD DATA WHEN OPEN
// -----------------------------
useEffect(() => {
  if (show && studentId) {
    loadLedger();
    loadLastReceipt();
  }
}, [show, studentId]);


// -----------------------------
// RESET WHEN STUDENT CHANGES
// -----------------------------
useEffect(() => {
  setReceiptData(null);
  setLastReceipt(null);
  setSelected({});
  setPaying({});
  setSelectAll(false);
}, [studentId]);

// -------------------------------
// Pay Checked Handler
// -------------------------------
const autoFillChecked = () => {

  const updatedPaying = { ...paying };

  ledger.forEach(row => {
    if (selected[row.ledger_id] && row.balance > 0) {
      updatedPaying[row.ledger_id] = row.balance;
    }
  });

  setPaying(updatedPaying);
};




// -----------------------------
// LOAD LEDGER
// -----------------------------
const loadLedger = async () => {
  try {
    setLoading(true);

    const res = await axios.get(
      `${config.apiBaseUrl}/students/get-ledger.php?student_id=${studentId}`
    );

    if (res.data.success) {
      setLedger(res.data.data || []);
    } else {
      setLedger([]);
    }

  } catch (err) {
    console.error(err);
    Swal.fire("Error", "Failed to load ledger", "error");
  } finally {
    setLoading(false);
  }
};


// -----------------------------
// LOAD LAST RECEIPT
// -----------------------------
const loadLastReceipt = async () => {
  try {
    const res = await axios.get(
      `${config.apiBaseUrl}/fees/get-last-receipt.php?student_id=${studentId}`
    );

    if (res.data.success && res.data.data) {
      const stored = JSON.parse(res.data.data.receipt_json);

      const receipt = {
        ...stored,
        student_name: studentName,
        class_name: studentClass
      };

      setReceiptData(receipt);
      setLastReceipt(receipt);

    } else {
      setReceiptData(null);
      setLastReceipt(null);
    }

  } catch (err) {
    console.error(err);
  }
};


// -----------------------------
// SELECT LOGIC
// -----------------------------
const toggleSelect = (ledgerId) => {
  const updated = {
    ...selected,
    [ledgerId]: !selected[ledgerId],
  };

  setSelected(updated);

  const unpaid = ledger.filter(r => r.balance > 0);
  const allChecked = unpaid.every(r => updated[r.ledger_id]);
  setSelectAll(allChecked);
};

const toggleSelectAll = () => {
  const newValue = !selectAll;
  setSelectAll(newValue);

  const updated = {};
  ledger.forEach(row => {
    if (row.balance > 0) updated[row.ledger_id] = newValue;
  });

  setSelected(updated);
};

const handlePayChange = (ledgerId, value) => {
  setPaying(prev => ({
    ...prev,
    [ledgerId]: value,
  }));
};

const totalSelected = ledger.reduce((sum, row) => {
  if (!selected[row.ledger_id]) return sum;
  return sum + parseFloat(paying[row.ledger_id] || 0);
}, 0);


// -----------------------------
// PAYMENT
// -----------------------------
const submitBatchPayment = async () => {

  const items = ledger
    .filter(row => selected[row.ledger_id])
    .map(row => ({
      ledger_id: row.ledger_id,
      fee_type: row.fee_type,
      amount: parseFloat(paying[row.ledger_id] || 0)
    }))
    .filter(i => i.amount > 0);

  if (items.length === 0) {
    Swal.fire("Invalid", "Select fees and enter amounts", "warning");
    return;
  }

  try {

    const res = await axios.post(
      `${config.apiBaseUrl}/fees/batch-payment.php`,
      { student_id: studentId, items }
    );

    if (res.data.success) {

      await Swal.fire({
        icon: "success",
        title: "Payment successful",
        text: `Receipt: ${res.data.receipt_no}`
      });

      const receipt = {
        receipt_no: res.data.receipt_no,
        student_name: studentName,
        class_name: studentClass,
        items,
        total: totalSelected,
        balance: Number(res.data.balance) || 0
      };

      setReceiptData(receipt);
      setLastReceipt(receipt);

      // auto print
      setTimeout(() => {
        printBtnRef.current?.click();
      }, 400);

      setPaying({});
      setSelected({});
      loadLedger();

    } else {
      Swal.fire("Error", res.data.error || "Payment failed", "error");
    }

  } catch (err) {
    console.error(err);
    Swal.fire("Error", "Server error", "error");
  }
};


  // -----------------------------
  // UI
  // -----------------------------
  return (
    <Modal show={show} onHide={onClose} size="xl" centered>

      <Modal.Header closeButton>
        <Modal.Title>
          Student Ledger — {studentName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center">Loading ledger...</div>
        ) : (
          <Table bordered hover>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Fee Type</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Pay</th>
              </tr>
            </thead>

            <tbody>
              {ledger.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">
                    No ledger found
                  </td>
                </tr>
              )}

              {ledger.map((row) => (
                <tr key={row.ledger_id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selected[row.ledger_id] || false}
                      onChange={() => toggleSelect(row.ledger_id)}
                      disabled={row.balance == 0}
                    />
                  </td>

                  <td>{row.fee_type}</td>
                  <td>{row.amount}</td>
                  <td>{row.paid}</td>
                  <td>{row.balance}</td>

                  <td>
                    <span className={`badge ${
                      row.status === "paid"
                        ? "bg-success"
                        : "bg-warning"
                    }`}>
                      {row.status}
                    </span>
                  </td>

                  <td style={{ width: 120 }}>
                    <Form.Control
                      type="number"
                      placeholder="Amount"
                      value={paying[row.ledger_id] || ""}
                      disabled={!selected[row.ledger_id]}
                      onChange={(e) =>
                        handlePayChange(row.ledger_id, e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>

        <div className="me-auto fw-bold">
          Total Selected: ₹{totalSelected.toFixed(2)}
        </div>
        <Button
            variant="outline-primary"
            onClick={autoFillChecked}
          >
            Auto-fill Checked
        </Button>

        <Button
          variant="success"
          onClick={submitBatchPayment}
          disabled={totalSelected <= 0}
        >
          Pay Selected
        </Button>
        {canReprint && (
          <Button
            variant="primary"
            onClick={() => printBtnRef.current?.click()}
          >
            Reprint Receipt
          </Button>
        )}


        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>

      </Modal.Footer>

      {/* Hidden print */}
      <div style={{ display: "none" }}>
        <ThermalReceipt
          ref={receiptRef}
          data={receiptData}
          logoUrl={logoUrl}
        />
        
        <ReactToPrint
          trigger={() => <span ref={printBtnRef} />}
          content={() => receiptRef.current}
        />
      </div>

    </Modal>
  );
};

export default StudentLedgerModal;
